import ButtonsToGenerateReports from '@/components/Reports/ButtonsToGenerateReports';
import FinancialCashFlowForm from '@/components/Reports/FinancialReports/FinancialCashFlowForm';
import { DefaultRouteTabs } from '@/components/_common';
import { useFinancialCashFlow } from '@/core/domain/NetReport/Financial/Financial.hooks';
import {
  FinancialCashFlowFormValues,
  FinancialCashFlowRequest,
  Situation,
  TypeReport,
} from '@/core/domain/NetReport/Financial/Financial.types';
import useFileLoading, { FileType } from '@/hooks/useFileLoading';
import { ReportCallback } from '@/hooks/useReportGenerate';
import { useReportHandlers } from '@/hooks/useReportHandler';
import { PageProps } from '@/types/PageProps';
import { DateTimeFormat } from '@/utils/moment';
import { Divider, Form } from 'antd';
import { Helmet } from 'react-helmet';
import { useIntl } from 'react-intl';

export default function FinancialCashFlowPage({ route }: PageProps) {
  const [form] = Form.useForm<FinancialCashFlowFormValues>();
  const typeReport = Form.useWatch('typeReport', form);

  const { setFileLoading, setFileUnloading, fileLoading } = useFileLoading();

  const generateReport = useFinancialCashFlow();

  const handleGenerateReport = async (
    format: FileType,
    callback?: ReportCallback,
  ) => {
    setFileLoading(format);

    try {
      const values = await form.validateFields();

      const obj: FinancialCashFlowRequest = {
        ...values,
        responseType: format === 'REPORT_VIEW' ? 'PDF' : format,
        companiesIds: values.companiesIds?.toString(),
        startDate: values.startDate.format(DateTimeFormat),
        endDate: values.endDate
          ? values.endDate.format(DateTimeFormat)
          : undefined,
        isOpen: values.situation.includes(Situation.open),
        isSettled: values.situation.includes(Situation.liquidated),
        bankAccountIds: values.bankAccountIds?.toString(),
      };

      const result = await generateReport.mutateAsync(obj);

      if (callback) {
        callback(result);
      }
    } catch {}

    setFileUnloading(format);
  };

  const { handleView, handlePdf, handleCsv } = useReportHandlers(
    'relatorio_fluxo_de_caixa',
    handleGenerateReport,
  );

  return (
    <DefaultRouteTabs route={route.parent} permissions={route.permissions}>
      <Helmet title={useIntl().formatMessage({ id: route.name })} />

      <FinancialCashFlowForm form={form} />

      <Divider />

      <ButtonsToGenerateReports
        viewReport={typeReport === TypeReport.daily ? undefined : handleView}
        generatePdf={typeReport === TypeReport.daily ? undefined : handlePdf}
        generateCsv={handleCsv}
        loading={fileLoading}
      />
    </DefaultRouteTabs>
  );
}
